import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { type FizboListing } from '@/hooks/useFizbo';

interface PdfSunumProps {
  listing: FizboListing;
}

const templateOptions = [
  { id: 'kurumsal', label: 'Kurumsal', desc: 'Profesyonel, sade tasarım', icon: 'ri-building-line' },
  { id: 'modern', label: 'Modern', desc: 'Renkli, dikkat çekici', icon: 'ri-layout-line' },
  { id: 'minimal', label: 'Minimal', desc: 'Temiz, minimalist', icon: 'ri-file-text-line' },
];

// Normalize Turkish chars for jspdf (helvetica font doesn't support Turkish)
function normalizeTR(text: string): string {
  if (!text) return '';
  return text
    .replace(/İ/g, 'I')
    .replace(/ı/g, 'i')
    .replace(/Ş/g, 'S')
    .replace(/ş/g, 's')
    .replace(/Ğ/g, 'G')
    .replace(/ğ/g, 'g')
    .replace(/Ü/g, 'U')
    .replace(/ü/g, 'u')
    .replace(/Ö/g, 'O')
    .replace(/ö/g, 'o')
    .replace(/Ç/g, 'C')
    .replace(/ç/g, 'c')
    .replace(/Â/g, 'A')
    .replace(/â/g, 'a')
    .replace(/Î/g, 'I')
    .replace(/î/g, 'i')
    .replace(/Û/g, 'U')
    .replace(/û/g, 'u');
}

// Professional marketing content sections
const marketingSections = [
  {
    title: 'Kurumsal Uyelik Gucu',
    icon: 'ri-award-line',
    content: [
      'Bireysel ilanlarin binlerce secenek arasinda geride kaldigi sistemde, Kurumsal Ofis Yetkimiz sayesinde ilaninizin her zaman profesyonel, guvenilir ve prestijli bir statude one cikmasini sagliyoruz.',
      'Aktif Listeleme Disiplini: Ilaninizin dijital dunyada "tozlanmasina" asla izin vermiyoruz; ofis ici onceliklendirme araclarimizi kullanarak ilaninizi duzenli olarak ust siralara tasiyor ve gorunurlugu her zaman en yuksek seviyede tutuyoruz.',
    ],
  },
  {
    title: 'Teknoloji ve Yazilim Gucuyle Dijital Vitrin',
    icon: 'ri-cpu-line',
    content: [
      'Aylik 10.000 TL+ Yazilim Yatirimi: Ilanlarinizi sadece yayina almiyor; Canva Pro, profesyonel video kurgu ve AI tabanli tasarim programlarina her ay duzenli yatirim yaparak mulkunuzun gorsel sunumunu bir marka projesi titizligiyle hazirliyoruz.',
      'Reklam Ajansi Kalitesinde Prodüksiyon: Evinizin sadece fotograflarini cekmiyoruz; aliciyi duygusal olarak yakalayan, mulkun ruhunu yansitan sinematik kurgular ve yuksek cozunurluklu dijital katalog calismalari hazirliyoruz.',
      'Yapay Zeka Destekli Algoritma Yonetimi: Yazilimlarimiz sayesinde mulkunuzun en cok hangi saatlerde ve hangi gorsellerle etkilesim aldigini analiz ediyor, pazarlama stratejimizi bu verilere gore anlik olarak optimize ediyoruz.',
    ],
  },
  {
    title: 'Global ve Yerel Reklam Dominasyonu',
    icon: 'ri-megaphone-line',
    content: [
      'Yillik 2 Milyon TL Meta Reklam Butcesi: Mulkunuzu sadece ilan sitelerinin insafina birakmiyoruz; Facebook ve Instagram\'da yonettigimiz dev butceli reklam kampanyalariyla, gayrimenkul aramasi yapan binlerce potansiyel alicinin ekranina profesyonelce konuk oluyoruz.',
      'Nokta Atisi Hedefleme Teknolojisi: Reklamlarimizi "herkese" degil, veri madenciligi kullanarak son donemde mulkunuzun bulundugu lokasyonda gayrimenkul aramasi ve satin alma gucu yuksek olan "gercek alici" kitlesine odakliyoruz.',
    ],
  },
  {
    title: 'Finansal Hiz ve Alici Operasyonu',
    icon: 'ri-bank-line',
    content: [
      'Ozel Banka Anlasmalari & VIP Kredi Hatti: Alicinizin kredi onayi icin banka banka gezmesine gerek kalmiyor; ozel protokollerimizle krediye erken ve kolay erisim saglayarak satisin onundeki en buyuk engel olan finansman sorununu en bastan cozuyoruz.',
      'Nitelikli Alici Eslestirme: Sistemimize kayitli olan binlerce aktif alici portfoyumuz icinden, mulkunuzun ozelliklerine en uygun profilleri aninda tespit ediyor ve "nitelikli randevu" sistemimizle sadece ciddi adaylari mulkunuze getiriyoruz.',
      'Pazar Veri Analizi: Bolgedeki guncel satis hizlarini ve aktif rakip ilanlarin durumunu analiz ederek, mulkunuzun pazarin "en cazip" secenegi haline gelmesi icin gereken tum stratejik hamleleri yapiyoruz.',
    ],
  },
  {
    title: 'Gorsel ve Teknik Ustunluk',
    icon: 'ri-camera-3-line',
    content: [
      '4K Drone ve Profesyonel Cekim: Mulkunuzun dis cevresini, konumsal avantajlarini ve mimari detaylarini profesyonel ekipmanlarla, kus bakisi ve sanatsal bir aciyla belgeliyoruz.',
      'Ucretsiz Ekspertiz: Guncel piyasa verilerini referans alarak, mulkunuzun satilabilir en dogru ve en yuksek degerini belirliyoruz.',
    ],
  },
];

export default function PdfSunum({ listing }: PdfSunumProps) {
  const { isDark } = useTheme();
  const [selectedTemplate, setSelectedTemplate] = useState('kurumsal');
  const [includeMap, setIncludeMap] = useState(true);
  const [includeCompetitor, setIncludeCompetitor] = useState(true);
  const [includeScore, setIncludeScore] = useState(true);
  const [includeMarketing, setIncludeMarketing] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);

  const addWrappedText = (
    doc: any,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number
  ): number => {
    const lines = doc.splitTextToSize(normalizeTR(text), maxWidth);
    doc.text(lines, x, y);
    return y + lines.length * lineHeight;
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenerated(false);

    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const pageW = 210;
      const pageH = 297;
      const margin = 20;

      // ============== PAGE 1 ==============
      // Header background
      doc.setFillColor(16, 185, 129);
      doc.rect(0, 0, pageW, 45, 'F');

      // Title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text(normalizeTR('Fırat Bilgin'), margin, 18);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(normalizeTR('Continental Gayrimenkul'), margin, 26);

      doc.setFontSize(9);
      doc.text(
        normalizeTR(`Sunum Tarihi: ${new Date().toLocaleDateString('tr-TR')}`),
        margin,
        34
      );
      doc.text(
        normalizeTR(`Ilan No: ${listing.listingNo}`),
        pageW - margin - 50,
        34
      );

      // Property Title
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(
        normalizeTR(`${listing.propertyType} - ${listing.transactionType}`),
        margin,
        60
      );

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(normalizeTR(listing.location), margin, 68);

      // Divider
      doc.setDrawColor(230, 230, 230);
      doc.line(margin, 74, pageW - margin, 74);

      // Info Grid
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      const infoItems = [
        ['Mal Sahibi', listing.ownerName],
        ['Telefon', listing.phone],
        ['Mulk Turu', listing.propertyType],
        ['Islem Turu', listing.transactionType],
        ['Ilan Yasi', `${listing.daysOld} Gun`],
        ['Kayit Tarihi', listing.createdAt],
        ...(listing.roomCount ? [['Oda Sayisi', listing.roomCount]] : []),
      ];

      infoItems.forEach((item, i) => {
        const col = i % 2 === 0 ? margin : pageW / 2 + 5;
        const row = 82 + Math.floor(i / 2) * 12;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(80, 80, 80);
        doc.text(normalizeTR(`${item[0]}:`), col, row);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30, 30, 30);
        doc.text(normalizeTR(item[1]), col + 28, row);
      });

      const baseY = 82 + Math.ceil(infoItems.length / 2) * 12;

      // Price Section
      doc.setFillColor(245, 245, 245);
      doc.roundedRect(margin, baseY, pageW - margin * 2, 22, 3, 3, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(16, 185, 129);
      doc.text(normalizeTR('FIYAT'), margin + 5, baseY + 12);
      doc.setFontSize(14);
      doc.setTextColor(30, 30, 30);
      const priceText = listing.price
        ? `${listing.price.toLocaleString('tr-TR')} TL`
        : 'Belirtilmemis';
      doc.text(normalizeTR(priceText), margin + 25, baseY + 12);

      // Sellability Score
      if (includeScore && listing.sellabilityScore) {
        doc.setFillColor(240, 253, 244);
        doc.roundedRect(margin, baseY + 28, (pageW - margin * 2) / 2 - 3, 28, 3, 3, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(16, 185, 129);
        doc.text(normalizeTR('SATILABILIRLIK SKORU'), margin + 5, baseY + 38);
        doc.setFontSize(20);
        doc.setTextColor(30, 30, 30);
        doc.text(`${listing.sellabilityScore}/100`, margin + 5, baseY + 50);
      }

      // Notes
      const notesY = baseY + 62;
      doc.setFillColor(250, 250, 250);
      doc.roundedRect(margin, notesY, pageW - margin * 2, 35, 3, 3, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(80, 80, 80);
      doc.text(normalizeTR('NOTLAR'), margin + 5, notesY + 9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      const noteLines = doc.splitTextToSize(
        normalizeTR(listing.notes || 'Not eklenmemis.'),
        pageW - margin * 2 - 10
      );
      doc.text(noteLines.slice(0, 3), margin + 5, notesY + 17);

      // Competitor section
      if (includeCompetitor) {
        const compY = notesY + 42;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 30, 30);
        doc.text(normalizeTR('RAKIP PIYASA OZETI'), margin, compY);
        doc.setDrawColor(16, 185, 129);
        doc.line(margin, compY + 3, margin + 55, compY + 3);

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text('Bolgedeki ortalama fiyat: 2.940.000 TL', margin, compY + 10);
        doc.text('Ortalama satis suresi: 35 gun', margin, compY + 17);
        doc.text('Aktif rakip ilan sayisi: 4', margin, compY + 24);
      }

      // Footer Page 1
      doc.setFillColor(245, 245, 245);
      doc.rect(0, pageH - 20, pageW, 20, 'F');
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        normalizeTR('Fırat Bilgin Continental Gayrimenkul'),
        margin,
        pageH - 10
      );
      doc.text('Sayfa 1/2', pageW - margin - 20, pageH - 10);

      // ============== PAGE 2 - Professional Marketing ==============
      if (includeMarketing) {
        doc.addPage();

        // Header
        doc.setFillColor(16, 185, 129);
        doc.rect(0, 0, pageW, 28, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(normalizeTR('Profesyonel Pazarlama Sunumu'), margin, 18);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(normalizeTR('Fırat Bilgin Continental Gayrimenkul'), pageW - margin - 65, 18);

        let currentY = 38;

        marketingSections.forEach((section) => {
          // Check if we need a new page
          if (currentY > pageH - margin - 40) {
            doc.addPage();
            currentY = 20;
          }

          // Section title
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(16, 185, 129);
          doc.text(normalizeTR(section.title), margin, currentY);
          currentY += 6;

          // Accent line
          doc.setDrawColor(16, 185, 129);
          doc.line(margin, currentY, margin + 40, currentY);
          currentY += 5;

          // Section content
          doc.setFontSize(8.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(60, 60, 60);

          section.content.forEach((paragraph) => {
            if (currentY > pageH - margin - 20) {
              doc.addPage();
              currentY = 20;
            }
            const lines = doc.splitTextToSize(normalizeTR(`• ${paragraph}`), pageW - margin * 2);
            doc.text(lines, margin, currentY);
            currentY += lines.length * 4.2 + 3;
          });

          currentY += 4;
        });

        // Footer Page 2
        doc.setFillColor(245, 245, 245);
        doc.rect(0, pageH - 20, pageW, 20, 'F');
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          normalizeTR('Fırat Bilgin Continental Gayrimenkul'),
          margin,
          pageH - 10
        );
        doc.text('Sayfa 2/2', pageW - margin - 20, pageH - 10);
      }

      doc.save(`${listing.listingNo}-sunum.pdf`);
      setGenerated(true);
    } catch (err) {
      console.error('PDF generation error:', err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Template Selection */}
      <div>
        <p className={`text-xs font-semibold mb-2 uppercase tracking-wide ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          Sablon Sec
        </p>
        <div className="grid grid-cols-3 gap-2">
          {templateOptions.map((t) => (
            <button
              key={t.id}
              onClick={() => setSelectedTemplate(t.id)}
              className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                selectedTemplate === t.id
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : isDark
                  ? 'border-dark-border hover:border-dark-muted'
                  : 'border-light-border hover:border-light-muted'
              }`}
            >
              <div className="w-6 h-6 flex items-center justify-center mb-1">
                <i className={`${t.icon} text-sm ${selectedTemplate === t.id ? 'text-primary-500' : isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
              </div>
              <p className={`text-xs font-semibold ${selectedTemplate === t.id ? 'text-primary-500' : isDark ? 'text-dark-text' : 'text-light-text'}`}>
                {t.label}
              </p>
              <p className={`text-xs mt-0.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{t.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Content Options */}
      <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <p className={`text-xs font-semibold mb-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          Icerik Secenekleri
        </p>
        <div className="space-y-2.5">
          {[
            { label: 'Satilabilirlik Skoru', value: includeScore, setter: setIncludeScore, icon: 'ri-bar-chart-line' },
            { label: 'Rakip Ilan Karsilastirmasi', value: includeCompetitor, setter: setIncludeCompetitor, icon: 'ri-bar-chart-grouped-line' },
            { label: 'Harita & Konum', value: includeMap, setter: setIncludeMap, icon: 'ri-map-pin-line' },
            { label: 'Profesyonel Pazarlama Sunumu', value: includeMarketing, setter: setIncludeMarketing, icon: 'ri-megaphone-line' },
          ].map((opt) => (
            <div key={opt.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${opt.icon} text-xs text-primary-500`} />
                </div>
                <span className={`text-xs ${isDark ? 'text-dark-text' : 'text-light-text'}`}>{opt.label}</span>
              </div>
              <button
                onClick={() => opt.setter(!opt.value)}
                className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${opt.value ? 'bg-primary-500' : isDark ? 'bg-dark-border' : 'bg-light-border'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${opt.value ? 'left-5' : 'left-0.5'}`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Preview Info */}
      <div className={`rounded-xl p-4 border-2 border-dashed ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
            <i className="ri-file-pdf-line text-primary-500" />
          </div>
          <div>
            <p className={`text-sm font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              {listing.listingNo}-sunum.pdf
            </p>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              A4 · {selectedTemplate === 'kurumsal' ? 'Kurumsal' : selectedTemplate === 'modern' ? 'Modern' : 'Minimal'} Sablon
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {['Ilan Bilgileri', 'Fiyat', 'Notlar', ...(includeScore ? ['Skor'] : []), ...(includeCompetitor ? ['Rakip Analiz'] : []), ...(includeMap ? ['Konum'] : []), ...(includeMarketing ? ['Pazarlama Sunumu'] : [])].map((tag) => (
            <span key={tag} className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-dark-border text-dark-muted' : 'bg-light-border text-light-muted'}`}>
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={generating}
        className={`w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer ${
          generating
            ? 'bg-gray-300 dark:bg-dark-border text-gray-500 cursor-not-allowed'
            : 'bg-primary-500 hover:bg-primary-600 text-white'
        }`}
      >
        {generating ? (
          <>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin" />
            </div>
            PDF Hazirlaniyor...
          </>
        ) : (
          <>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-download-line" />
            </div>
            PDF Sunum Indir
          </>
        )}
      </button>

      {generated && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-checkbox-circle-line" />
          </div>
          PDF basariyla indirildi!
        </div>
      )}
    </div>
  );
}